import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import InlineKeepTogether from "../../../../components/ui/InlineKeepTogether";
import { checkFractionAnswer, latexFrac, makeUid, pick, simplify } from "./probabilityEventUtils";

function range(from, to) {
  return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

function makeData() {
  for (let attempt = 0; attempt < 100; attempt += 1) {
    const denominator = pick([20, 24, 30, 40]);
    const intersection = pick([2, 3, 4, 5, 6]);
    const values = range(intersection + 2, denominator - 5);
    const a = pick(values);
    const b = pick(values.filter((v) => v !== a));

    if (b && a + b - intersection < denominator) {
      const answer = simplify(a + b - intersection, denominator);
      return { denominator, a, b, intersection, answer };
    }
  }

  const denominator = 20;
  const a = 9;
  const b = 8;
  const intersection = 3;
  const answer = simplify(a + b - intersection, denominator);
  return { denominator, a, b, intersection, answer };
}

export function generateTask() {
  return {
    type: "UnionProbabilityTask",
    uid: makeUid("prob_union"),
    data: makeData()
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      Dla zdarzeń <InlineMath math="A" /> i <InlineMath math="B" /> mamy{" "}
      <InlineMath math={`P(A)=${latexFrac(data.a, data.denominator)}`} />,{" "}
      <InlineMath math={`P(B)=${latexFrac(data.b, data.denominator)}`} /> oraz{" "}
      <InlineKeepTogether>
        <InlineMath math={`P(A\\cap B)=${latexFrac(data.intersection, data.denominator)}`} />.
      </InlineKeepTogether>{" "}
      Oblicz{" "}
      <InlineKeepTogether>
        <InlineMath math={"P(A\\cup B)"} />.
      </InlineKeepTogether>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <InlineMath math={"P(A\\cup B) = "} />
      <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label={"P(A\\cup B)"} placeholder="np. 3/4" />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return checkFractionAnswer(data.answer, userAnswer);
}

export function renderAnswer({ data }) {
  return <InlineMath math={`P(A\\cup B)=${latexFrac(data.answer[0], data.answer[1])}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw, "P(A\\cup B)")} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Dla sumy dwóch zdarzeń odejmujemy część wspólną, żeby nie policzyć jej dwa razy.</p>
      <BlockMath math={"P(A\\cup B)=P(A)+P(B)-P(A\\cap B)"} />
      <p><strong>Krok 2.</strong> Podstawiamy dane.</p>
      <BlockMath math={`P(A\\cup B)=${latexFrac(data.a, data.denominator)}+${latexFrac(data.b, data.denominator)}-${latexFrac(data.intersection, data.denominator)}=${latexFrac(data.answer[0], data.answer[1])}`} />
      <p><strong>Odpowiedź:</strong> <InlineKeepTogether><InlineMath math={`P(A\\cup B)=${latexFrac(data.answer[0], data.answer[1])}`} />.</InlineKeepTogether></p>
    </div>
  );
}
